import SparkMD5 from 'spark-md5';
import { normalizeApiEndpoint } from './normalize-api-endpoint';
import { reportError } from './report-error';

const MAX_ITEMS = 30;
const SAFE_FIELD = /^[a-zA-Z_][a-zA-Z0-9_]{0,63}$/;
const SAFE_CODE = /^[a-zA-Z0-9_][a-zA-Z0-9_.-]{0,63}$/;
const ENVELOPE_KEYS = new Set(['detail', 'message', 'error', 'errors']);

function validationContext(details: unknown) {
  const fields = new Set<string>();
  const codes = new Set<string>();
  let remaining = 200;

  function visit(value: unknown, path: string, depth: number, isCode = false) {
    if (depth > 6 || remaining-- <= 0) return;
    if (typeof value === 'string') {
      if (path && fields.size < MAX_ITEMS) fields.add(path);
      if (isCode) {
        if (SAFE_CODE.test(value) && codes.size < MAX_ITEMS) codes.add(value);
      } else {
        // Backend messages may contain submitted values. Keep only numeric codes.
        for (const match of value.slice(0, 2048).matchAll(/\((\d{1,10})\)/g)) {
          if (codes.size < MAX_ITEMS) codes.add(match[1]);
        }
      }
      return;
    }
    if (isCode && typeof value === 'number' && Number.isSafeInteger(value)) {
      if (path && fields.size < MAX_ITEMS) fields.add(path);
      if (value >= 0 && value <= 9999999999 && codes.size < MAX_ITEMS) {
        codes.add(String(value));
      }
      return;
    }
    if (!value || typeof value !== 'object') return;
    if (Array.isArray(value)) {
      for (const item of value.slice(0, MAX_ITEMS)) {
        visit(item, path, depth + 1, isCode);
      }
      return;
    }
    for (const key in value) {
      if (remaining <= 0) break;
      if (!Object.hasOwn(value, key)) continue;
      const child = (value as Record<string, unknown>)[key];
      if (key === 'code' || key === 'codes') {
        visit(child, path, depth + 1, true);
      } else if (ENVELOPE_KEYS.has(key)) {
        visit(child, path, depth + 1);
      } else if (SAFE_FIELD.test(key)) {
        const field = path ? `${path}.${key}` : key;
        if (field.length <= 128) visit(child, field, depth + 1);
      }
      remaining--;
    }
  }

  visit(details, '', 0);
  return {
    error_kind: fields.size ? 'validation' : 'bad_request',
    validation_fields: [...fields],
    validation_codes: [...codes],
  };
}

function getErrorKind(
  status: number,
  validation: ReturnType<typeof validationContext>
) {
  if (status === 400) return validation.error_kind;
  if (status === 0) return 'network';
  if (status >= 500 && status <= 599) return 'server';
  return 'http';
}

export function reportApiError({
  source,
  url,
  method,
  details,
  status,
}: {
  source: 'legacy-api' | 'construct-api';
  url: string;
  method: string;
  details?: unknown;
  status: number;
}): void {
  let endpoint = '/unknown';
  try {
    const parsed = new URL(url, 'https://api.invalid');
    if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
      endpoint = parsed.pathname;
    }
  } catch {
    // An invalid URL must not prevent the original API error from reaching forms.
  }
  const requestMethod = method.toUpperCase();
  const normalizedEndpoint = normalizeApiEndpoint(endpoint);
  const validation = validationContext(status === 400 ? details : undefined);
  const errorKind = getErrorKind(status, validation);
  // Hash the canonical dimensions to keep fingerprints bounded without including
  // raw response values. Source is deliberately omitted for shared API operations.
  const fingerprint = SparkMD5.hash(
    JSON.stringify([
      requestMethod,
      normalizedEndpoint,
      status,
      validation.validation_codes.length ? 'codes' : 'fields',
      [
        ...(validation.validation_codes.length
          ? validation.validation_codes
          : validation.validation_fields),
      ].sort(),
    ])
  );
  const error = Object.assign(
    new Error(
      `${status === 0 ? 'Network failure' : status} ${requestMethod} ${normalizedEndpoint}`
    ),
    { dd_fingerprint: `api-v1:${fingerprint}` }
  );
  reportError(source, error, {
    resource: { status_code: status, method: requestMethod, url: endpoint },
    api: { ...validation, endpoint: normalizedEndpoint, error_kind: errorKind },
  });
}
